import { PageHero, SectionHead, CTABand } from "@/components/PageHero";
import { Reveal, ParallaxImg } from "@/components/anim";
import { IMG } from "@/lib/img";

const OPTIONS = [
  {
    name: "Rack Rental",
    desc: "Dedicated clothing rack inside Essandtials.",
    price: "$250/month",
  },
  {
    name: "Premium Rack + Display",
    desc: "Clothing rack plus shelf or display area.",
    price: "$350/month",
  },
  {
    name: "Shelf Rental",
    desc: "Perfect for beauty products, accessories, jewelry, candles, home products, and small merchandise.",
    price: "$150/month",
  },
  {
    name: "Tabletop Display",
    desc: "Ideal for smaller brands and product collections.",
    price: "$125/month",
  },
  {
    name: "Window Feature",
    desc: "Premium front-window placement for featured brands.",
    price: "$200/week",
  },
];

export default function ShopWithinShop() {
  return (
    <>
      <PageHero
        eyebrow="Shop Within a Shop"
        lines={[
          <>Your Brand. Your Space.</>,
          <>
            <em>Without the Overhead.</em>
          </>,
        ]}
        sub="Monthly rental options for businesses that do not need the entire store. Put your brand inside Essandtials and sell to every customer who walks in."
      />

      <section className="bg-paper pb-24 sm:pb-32" data-testid="swis-options">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {OPTIONS.map((o, i) => (
              <Reveal key={o.name} delay={i * 0.06}>
                <div
                  className="flex h-full flex-col rounded-3xl border border-beige/60 bg-white/80 p-8 shadow-[0_4px_24px_rgba(26,24,23,0.04)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(26,24,23,0.1)]"
                  data-testid={`swis-option-${i + 1}`}
                >
                  <h3 className="font-serif text-2xl uppercase text-ink">{o.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-clay">{o.desc}</p>
                  <p className="mt-6 font-serif text-xl italic text-sanddeep">{o.price}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex h-full flex-col justify-between rounded-3xl bg-ink p-8 text-paper">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.28em] text-blushdeep">
                    Hands-free retail
                  </p>
                  <h3 className="mt-5 font-serif text-2xl uppercase">Managed Retail</h3>
                  <p className="mt-3 text-sm leading-relaxed text-paper/70">
                    The business supplies the inventory and Essandtials handles customer sales
                    during store operating hours for an additional management fee or commission.
                  </p>
                </div>
                <p className="mt-6 font-serif text-xl italic text-blushdeep">Ask us for details</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-sand py-24 sm:py-32" data-testid="swis-feature">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
          <ParallaxImg
            src={IMG.store.shelving}
            alt="Inside Essandtials — wooden shelving ready for product displays"
            className="aspect-[4/5] w-full rounded-3xl shadow-[0_30px_80px_rgba(26,24,23,0.12)]"
          />
          <div>
            <SectionHead
              eyebrow="Small space. Big presence."
              title={
                <>
                  Be a brand
                  <br />
                  <em>people can touch.</em>
                </>
              }
              sub="A rack, a shelf, a tabletop, or the front window — your products live in a real retail environment, merchandised beautifully, while you keep your overhead close to zero."
            />
          </div>
        </div>
      </section>

      <CTABand
        title={
          <>
            Ready to hang your brand
            <br />
            <em>inside Essandtials?</em>
          </>
        }
        cta="Become an Essandtials Brand"
        to="/book?type=Rack%20Rental"
        testid="swis-book-button"
      />
    </>
  );
}
